/* eslint-disable react/button-has-type */

/* eslint-disable react/prop-types */

/* eslint-disable @next/next/no-img-element */
import React, { useContext } from "react";

import classNames from "classnames";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import {
  CustomRightArrow,
  CustomLeftArrow,
  CarouselArrows,
} from "./CarouselArrows";
import TextItemWithPreview from "./TextItemWithPreview";

const StyledWrapper = styled.div`
  ${(props) => props.dynamicStyles}
  width: 100%;
  max-width: 100%;
  position: relative;
  ${(props) =>
    props.fullWidth
      ? `
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  `
      : ""}

  .react-multi-carousel-item {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    ${(props) =>
      props.variableWidth
        ? `
   width: auto !important;
    flex-grow: 0 !important;
    flex-shrink: 0 !important;
  `
        : ""}
  }

  .custom-dot-list-style {
    margin-top: 20px;
  }

  .custom-dot-list-style button {
    border: none;
    background: ${(props) => props.dotColor || "#ddd"};
    width: ${(props) => props.dotSize || 10}px;
    height: ${(props) => props.dotSize || 10}px;
    border-radius: 50%;
    margin: 0 5px;
    padding: 0;
    transition: background 0.3s;
  }

  .custom-dot-list-style .react-multi-carousel-dot--active button {
    background: ${(props) => props.activeDotColor || "#666"};
  }

  .carousel-container {
    padding-bottom: 40px;
  }
`;

const StyledImageContainer = styled.div`
  padding: ${(props) => props.itemPadding || "0 15px"};
  margin-bottom: ${(props) => props.itemMarginBottom || "30px"};
  width: ${(props) => (props.variableWidth ? "auto" : "100%")};
  height: auto;
  ${(props) =>
    props.variableWidth && props.imageWidth
      ? `width: ${props.imageWidth}px;` // <-- This is the item's custom width
      : ""}
  ${(props) =>
    props.variableWidth && props.imageWidth
      ? `width: ${props.imageWidth}px;`
      : ""}

  /* Reduce gaps between items in variable width mode */
  ${(props) =>
    props.variableWidth
      ? `
    padding: ${props.itemPadding || "0 2px"};
    margin-right: ${props.itemGap || "5px"};
  `
      : ""}
`;

const ImageWrapper = styled.div`
  position: relative;
  width: ${(props) => (props.variableWidth ? "100%" : "100%")};
  height: ${(props) => props.height || 250}px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: ${(props) =>
      props.mobileHeight ||
      (props.height ? `${props.height * 0.8}px` : "200px")};
  }

  @media (max-width: 480px) {
    height: ${(props) =>
      props.mobileSmallHeight ||
      (props.height ? `${props.height * 0.6}px` : "150px")};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: ${(props) => props.objectFit || "contain"};
    /* Fallback for older Safari that doesn't support object-fit */
    -webkit-object-fit: ${(props) => props.objectFit || "contain"};
  }
`;

function ImageCarousel() {
  const {
    // Core content fields
    title,
    image, // Array of images from Strapi
    uniqueId,
    wrapperClassName,
    CSS,

    // Display settings
    slidesPerView = "three",
    autoPlay = true,
    autoPlaySpeed = 3000,
    infiniteLoop = true,
    rtl = false,

    // Navigation
    showArrows = true,
    showDots = true,
    hideArrowsOnMobile = true,

    // Image display
    imageDisplay = "contain", // can be "contain", "cover", "fill"
    imageHeight = 250,
    mobileImageHeight, // Optional, will calculate from imageHeight if not provided
    mobileSmallImageHeight,
    variableWidth = false,

    // Custom responsive settings
    customResponsive = null,

    // Carousel spacing options
    itemPadding = "0 15px",
    itemMarginBottom = "30px",
    itemGap = "5px", // Gap between items in variable width mode

    // Dot styling
    dotSize = 10,
    dotColor = "#ddd",
    activeDotColor = "#666",

    // Arrow customization
    customLeftArrowContent,
    customRightArrowContent,
    customArrows = false,
    arrowSize = 24,
    arrowColor = "#fff",

    // Animation options
    transitionDuration = 500,
    customTransition = "transform 500ms ease-in-out",

    fullWidth = false,
    textItems = [],
  } = useContext(ComponentContext);

  // Map the string settings to actual numbers for default responsive settings
  const slidesMap = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
  };

  const slidesToShow = slidesMap[slidesPerView] || 3;

  // Define default responsive breakpoints for the carousel
  const defaultResponsive = variableWidth
    ? {
        // For variable width infinite loop, we need specific settings
        desktop: {
          breakpoint: { max: 3000, min: 1024 },
          items: slidesToShow,
          slidesToSlide: 1,
          partialVisibleGutter: 40,
        },
        tablet: {
          breakpoint: { max: 1024, min: 464 },
          items: Math.min(slidesToShow, 2),
          slidesToSlide: 1,
          partialVisibleGutter: 40,
        },
        mobile: {
          breakpoint: { max: 464, min: 0 },
          items: 1,
          slidesToSlide: 1,
          partialVisibleGutter: 40,
        },
      }
    : {
        desktop: {
          breakpoint: { max: 3000, min: 1024 },
          items: slidesToShow,
          slidesToSlide: 1,
        },
        tablet: {
          breakpoint: { max: 1024, min: 464 },
          items: Math.min(slidesToShow, 2),
          slidesToSlide: 1,
        },
        mobile: {
          breakpoint: { max: 464, min: 0 },
          items: 1,
          slidesToSlide: 1,
        },
      };

  // Use custom responsive settings if provided, otherwise use default
  let responsive = defaultResponsive;

  // Parse custom responsive settings if provided
  if (customResponsive) {
    try {
      // If customResponsive is already an object, use it directly
      if (typeof customResponsive === "object") {
        responsive = customResponsive;
      }
      // If customResponsive is a string (JSON), parse it
      else if (typeof customResponsive === "string") {
        responsive = JSON.parse(customResponsive);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error parsing custom responsive settings:", error);
      // Fallback to default responsive settings
      responsive = defaultResponsive;
    }
  }

  // Determine which device types should have arrows hidden
  const hideArrowsOn = [];
  if (!showArrows) {
    hideArrowsOn.push("desktop", "tablet", "mobile");
  } else if (hideArrowsOnMobile) {
    hideArrowsOn.push("tablet", "mobile");
  }

  // Process Strapi's image format
  const carouselImages = image || [];

  // For variable width infinite loop, duplicate images safely
  // Limit duplication to prevent memory issues and crashes
  const processedImages =
    variableWidth && carouselImages.length > 0 && infiniteLoop
      ? carouselImages.concat(carouselImages).concat(carouselImages)
      : carouselImages;

  // Don't render if no images and no text items
  if (!processedImages.length && !textItems.length) {
    return null;
  }

  return (
    <StyledWrapper
      className={classNames(wrapperClassName)}
      id={uniqueId}
      dynamicStyles={CSS}
      dotSize={dotSize}
      dotColor={dotColor}
      activeDotColor={activeDotColor}
      fullWidth={fullWidth}
      variableWidth={variableWidth}
    >
      <Carousel
        responsive={responsive}
        infinite={infiniteLoop && !variableWidth}
        removeArrowOnDeviceType={hideArrowsOn}
        autoPlay={autoPlay}
        autoPlaySpeed={autoPlaySpeed}
        rtl={rtl}
        variableWidth={variableWidth}
        keyBoardControl
        pauseOnHover={false}
        showDots={showDots}
        renderDotsOutside
        dotListClass="custom-dot-list-style"
        containerClass="carousel-container"
        swipeable
        draggable
        centerMode={false}
        customTransition={customTransition}
        transitionDuration={transitionDuration}
        arrows={!customArrows}
        shouldResetAutoplay={false}
        rewind={false}
        minimumTouchDrag={0}
        customRightArrow={
          !customArrows ? (
            <CustomRightArrow arrowSize={arrowSize} arrowColor={arrowColor} />
          ) : null
        }
        customLeftArrow={
          !customArrows ? (
            <CustomLeftArrow arrowSize={arrowSize} arrowColor={arrowColor} />
          ) : null
        }
        renderButtonGroupOutside={customArrows}
        customButtonGroup={
          customArrows && showArrows ? (
            <CarouselArrows
              leftArrowContent={customLeftArrowContent}
              rightArrowContent={customRightArrowContent}
            />
          ) : null
        }
      >
        {textItems?.length > 0
          ? textItems.map((item) => (
              <TextItemWithPreview
                dynamicStyles={item.textCSS}
                key={item.name}
                item={item}
                itemPadding={itemPadding}
                itemMarginBottom={itemMarginBottom}
              />
            ))
          : processedImages.map((img, index) => (
              <StyledImageContainer
                key={
                  img.id
                    ? `img-${img.id}-${index}`
                    : `img-${img?.url || index}-${index}`
                }
                itemPadding={itemPadding}
                itemMarginBottom={itemMarginBottom}
                itemGap={itemGap}
                variableWidth={variableWidth}
                imageWidth={img?.width || img?.customWidth}
              >
                <ImageWrapper
                  height={imageHeight}
                  mobileHeight={mobileImageHeight}
                  mobileSmallHeight={mobileSmallImageHeight}
                  objectFit={imageDisplay}
                  variableWidth={variableWidth}
                >
                  <img
                    alt={img.alternativeText || title || "Carousel image"}
                    src={img.url || ""}
                    onError={(e) => {
                      // eslint-disable-next-line no-console
                      console.error("Image failed to load:", img.url);
                      e.target.style.backgroundColor = "#f0f0f0";
                      e.target.style.display = "none";
                    }}
                    onLoad={(e) => {
                      // Ensure image displays after load
                      if (e.target.style.display === "none") {
                        e.target.style.display = "block";
                      }
                    }}
                  />
                </ImageWrapper>
              </StyledImageContainer>
            ))}
      </Carousel>
    </StyledWrapper>
  );
}

export default ImageCarousel;
